import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useRoute } from '@react-navigation/core';
import {
  StackNavigationOptions,
  StackNavigationProp,
} from '@react-navigation/stack';
import {
  FC,
  ReactNode,
  Suspense,
  createContext,
  memo,
  useContext,
} from 'react';
import { ScrollView, StyleProp, ViewStyle } from 'react-native';

type WrappedNavigatorScreenContextProps = {
  WrapperComponent?: FC<{ children?: ReactNode }>;
  contentContainerStyle?: StyleProp<ViewStyle>;
};

export const WrappedNavigatorScreenContext =
  createContext<WrappedNavigatorScreenContextProps>(null as any);

export const useScreenContentContainerStyle = () =>
  useContext(WrappedNavigatorScreenContext)?.contentContainerStyle;

export type WrappedNavigatorScreen<
  T extends Record<string, any>,
  U = StackNavigationOptions,
> = FC<T> & {
  component: FC<T>;
  options: U;
  extraProps?: any;
};

export function wrapNavigatorScreen<
  T extends object,
  U = StackNavigationOptions,
>(
  Component: FC<T>,
  options?:
    | U
    | ((props: {
        route: { params: T };
        navigation: StackNavigationProp<any>;
      }) => U),
  extraProps?: any,
  fallback?: NonNullable<ReactNode> | null,
): WrappedNavigatorScreen<T> {
  const NavigatorScreenWrapper = memo(function () {
    const ctx = useContext(WrappedNavigatorScreenContext);
    const maybeParams = useRoute().params as any;
    const params =
      maybeParams != null &&
      'path' in maybeParams &&
      'screen' in maybeParams &&
      'params' in maybeParams
        ? (maybeParams.params as T)
        : (maybeParams as T);
    const { navBarHeight } = useSystemMetrics();
    return (
      <Suspense
        fallback={
          fallback === undefined ? (
            <ScrollView
              css={`
                flex: 1;
              `}
              contentContainerStyle={[
                {
                  flexGrow: 1,
                  paddingTop: navBarHeight + 15,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                },
              ]}
            >
              <DefaultLoader />
            </ScrollView>
          ) : (
            fallback
          )
        }
      >
        {ctx?.WrapperComponent != null ? (
          <ctx.WrapperComponent>
            <Component {...params} />
          </ctx.WrapperComponent>
        ) : (
          <Component {...params} />
        )}
      </Suspense>
    );
  }) as any;

  NavigatorScreenWrapper.component = Component;
  NavigatorScreenWrapper.options = options as U;
  NavigatorScreenWrapper.extraProps = extraProps;

  return NavigatorScreenWrapper;
}
